import { FC, useEffect, useState } from 'react';
import { GridColDef, GridSortModel } from '@mui/x-data-grid';
import StyledGridContainer, {
  CenteredCellBox,
} from 'src/components/StyledComponents/StyledGridContainer';
import Status from 'src/components/Status';
import { rolePriorities, statusType } from 'src/utils/properties.utils';
import { capitalizeFirstLetter } from 'src/utils/text.utils';
import { formatLastLoggedInDate } from 'src/utils/format.utils';
import { User } from 'src/models/Management.interface';
import PaginationControl from 'src/components/StyledComponents/StyledPagination';
import CustomNoRowsOverlay from 'src/components/StyledComponents/CustomEmptyTableRow';
import LoadingTable from 'src/components/StyledComponents/CustomLoadingTable';
import StyledDataGrid from 'src/components/StyledComponents/StyledDataGrid';
import { getAllUsers } from 'src/services/user.service';
import UserActionMenuButton from 'src/components/ActionButtons/UserActionMenu';
import { pagination } from 'src/utils/properties.utils';
import { UnverifiedIcon, VerifiedIcon } from './UserManagementGrid.styled';

const UserManagementGrid: FC<{ state: boolean; searchTerm: string }> = ({
  state,
  searchTerm,
}) => {
  const [page, setPage] = useState(pagination.DEFAULT_PAGE);
  const [pageSize, setPageSize] = useState(pagination.DEFAULT_PAGE_SIZE);
  const [sortBy, setSortBy] = useState('firstName');
  const [order, setOrder] = useState<'ASC' | 'DESC'>('ASC');
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [renderValue, setRenderValue] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: 'fullName', sort: 'asc' },
  ]);

  useEffect(() => {
    fetchAndSetUsers();
  }, [state, renderValue, page, pageSize, sortBy, order, searchTerm]);

  useEffect(() => {
    setPage(pagination.DEFAULT_PAGE);
  }, [searchTerm]);

  const formatUser = (user: User, index: number) => {
    let roleDisplay = 'N/A';
    if (user.roles && user.roles.length > 0) {
      const roleNames = user.roles
        .map((role: string | { name: string }) =>
          typeof role === 'string'
            ? role.toLowerCase()
            : (role as { name: string }).name.toLowerCase()
        )
        .sort(
          (a, b) => (rolePriorities[a] ?? 999) - (rolePriorities[b] ?? 999)
        );
      roleDisplay = roleNames.map(capitalizeFirstLetter).join(', ');
    }
    return {
      ...user,
      id: user.id,
      number: (page - 1) * pageSize + index + 1,
      fullName: `${user.firstName} ${user.lastName}`,
      role: roleDisplay,
      lastLogin: formatLastLoggedInDate(user.lastLoggedInDate),
      status: user.isActive ? statusType[1] : statusType[2],
      isVerifiedStatus: user.isVerified ? 'Verified' : 'Unverified',
    };
  };

  const fetchAndSetUsers = async () => {
    setLoading(true);
    try {
      const response = await getAllUsers(
        page,
        pageSize,
        searchTerm,
        sortBy,
        order
      );
      setUsers(response.data);
      setTotalItems(response.totalItems);
    } catch {
      setUsers([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  };
  const handleSortModelChange = (newSortModel: GridSortModel) => {
    setSortModel(newSortModel);
    const order = (newSortModel[0]?.sort || 'asc').toUpperCase();
    const field = newSortModel[0]?.field || 'createdAt';

    const backendField =
      field === 'status'
        ? 'isActive'
        : field === 'fullName'
        ? 'firstName'
        : field === 'isVerifiedStatus'
        ? 'isVerified'
        : field === 'lastLogin'
        ? 'lastLoggedInDate'
        : field;

    setPage(1);
    setSortBy(backendField);
    setOrder(order as 'ASC' | 'DESC');
  };

  const columns: GridColDef[] = [
    {
      field: 'number',
      headerName: '#',
      flex: 0.3,
      headerAlign: 'center',
      align: 'center',
      sortable: false,
    },
    {
      field: 'fullName',
      headerName: 'Full Name',
      flex: 1.5,
      sortable: true,
    },
    { field: 'role', headerName: 'Role', flex: 1, sortable: false },
    { field: 'email', headerName: 'Email', flex: 1, sortable: true },
    {
      field: 'status',
      headerName: 'Status',
      flex: 0.5,
      headerAlign: 'center',
      align: 'center',
      sortable: true,
      renderCell: (params) => (
        <CenteredCellBox>
          <Status status={params.value} />
        </CenteredCellBox>
      ),
    },
    {
      field: 'isVerifiedStatus',
      headerName: 'Verified',
      flex: 0.5,
      headerAlign: 'center',
      align: 'center',
      sortable: true,
      renderCell: (params) => (
        <CenteredCellBox>
          {params.value === 'Verified' ? (
            <VerifiedIcon className="bx bx-check-circle" />
          ) : (
            <UnverifiedIcon className="bx bx-x-circle" />
          )}
        </CenteredCellBox>
      ),
    },
    {
      field: 'lastLogin',
      headerName: 'Last Logged In',
      flex: 1,
      sortable: true,
    },
    {
      field: 'action',
      headerName: 'Action',
      flex: 0.5,
      headerAlign: 'center',
      align: 'center',
      sortable: false,
      renderCell: (params) => {
        return (
          <UserActionMenuButton
            user={params.row}
            incrementRender={() => setRenderValue((prev) => prev + 1)}
          />
        );
      },
    },
  ];

  return loading ? (
    <LoadingTable columns={columns} />
  ) : (
    <StyledGridContainer
      pagination={
        totalItems > 0 ? (
          <PaginationControl
            totalItems={totalItems}
            page={page}
            setPage={setPage}
            pageSize={pageSize}
            setPageSize={setPageSize}
            loading={loading}
          />
        ) : undefined
      }
    >
      <StyledDataGrid
        rows={users.map(formatUser)}
        columns={columns}
        disableColumnMenu
        sortingMode="server"
        sortModel={sortModel}
        slots={{
          noRowsOverlay: CustomNoRowsOverlay,
        }}
        onSortModelChange={handleSortModelChange}
        sortingOrder={['asc', 'desc']}
      />
    </StyledGridContainer>
  );
};

export default UserManagementGrid;
